import type { AnyObjectSchema } from "yup";
import { FormProvider as CustomFormProvider } from "./form-provider";
import { yupResolver } from "@hookform/resolvers/yup";
import type {
  DefaultValues,
  SubmitHandler,
  UseFormReturn,
  ValidationMode,
} from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import React, { forwardRef, useEffect } from "react";

type FormProps<
  T extends Record<string, unknown>,
  TContext extends Record<string, unknown> = Record<string, unknown>
> = {
  className?: string;
  schema?: AnyObjectSchema;
  onSubmit: SubmitHandler<T>;
  mode?: keyof ValidationMode;
  defaultValues?: DefaultValues<T>;
  children:
    | ((
        methods: UseFormReturn<T, TContext> & {
          submitForm: () => void;
          form: React.RefObject<HTMLFormElement | null>;
        }
      ) => React.ReactElement)
    | React.ReactNode;
};

const FormInner = <
  T extends Record<string, unknown>,
  TContext extends Record<string, unknown> = Record<string, unknown>
>(
  {
    schema,
    children,
    onSubmit,
    className,
    defaultValues,
    mode = "onSubmit",
  }: FormProps<T, TContext>,
  ref: React.ForwardedRef<HTMLFormElement>
): React.ReactElement => {
  const methods = useForm<T, TContext>({
    mode,
    defaultValues,
    resolver: schema ? yupResolver(schema) : undefined,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  return (
    <FormProvider {...methods}>
      <CustomFormProvider form={ref as React.RefObject<HTMLFormElement>}>
        {({ form, submitForm }) => (
          <form
            ref={ref}
            autoComplete="off"
            className={className}
            onSubmit={methods.handleSubmit(onSubmit)}
          >
            {typeof children === "function"
              ? children({ ...methods, form, submitForm })
              : children}
          </form>
        )}
      </CustomFormProvider>
    </FormProvider>
  );
};

export const Form = forwardRef(FormInner) as <
  T extends Record<string, unknown>,
  TContext extends Record<string, unknown> = Record<string, unknown>
>(
  props: FormProps<T, TContext> & {
    ref?: React.Ref<HTMLFormElement>;
  }
) => ReturnType<typeof FormInner>;

export type { FormProps };
